import moment from 'moment-timezone'
import { addParamsToLocation } from '@smarttransit/common-client'
import { addAlert, isAuthorized, isOnlyInvestor as isOnlyInvestorFn } from '@/utilities/helpers'
import { getReportTypes } from '@/services/reports-service'
import { findTransportationOwners } from '@/services/transportation-owners-service'
import { findTransportationProfiles } from '@/services/transportation-profiles-service'

export default {
  name: 'reports',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      componentMounted: false,
      loadingFilters: false,
      reportTypes: [],
      accountOwners: [],
      vehicles: [],
      selectedReportType: 'distance_travelled',
      selectedAccountOwnerId: null,
      selectedVehicleIds: [],
      dateFrom: null,
      dateTo: null,
      lastParams: null,
      appliedAccountOwnerId: null,
      appliedFilters: null,
      isRestoringFilters: false
    }
  },
  computed: {
    getSignedInUser () {
      return this.$store.state.credentials.user
    },
    isStaffRole () {
      return isAuthorized(this.getSignedInUser, 'staff')
    },
    isTransporterRole () {
      return isAuthorized(this.getSignedInUser, 'transporter')
    },
    isOnlyInvestorRole () {
      return isOnlyInvestorFn(this.getSignedInUser)
    },
    isTransportationOwnerUser () {
      return this.isTransporterRole && !this.isStaffRole
    },
    showAccountOwnerFilter () {
      return !this.isTransportationOwnerUser && !this.isOnlyInvestorRole
    },
    reportTypeOptions () {
      return (this.reportTypes || []).map((item) => item.reportType).filter((value) => !!value)
    },
    accountOwnerOptions () {
      return (this.accountOwners || []).map((item) => ({
        text: item.companyName,
        value: item.id
      }))
    },
    vehicleOptions () {
      return (this.vehicles || []).map((item) => ({
        text: item.licensePlateNumber || item.id,
        value: item.id
      }))
    },
    hasPendingFilterChanges () {
      if (!this.appliedFilters) {
        return false
      }

      const selectedVehicleIds = Array.isArray(this.selectedVehicleIds) ? this.selectedVehicleIds : []
      const currentVehicleIds = selectedVehicleIds.map((id) => String(id)).sort()
      const appliedVehicleIds = (this.appliedFilters.selectedVehicleIds || []).map((id) => String(id)).sort()

      return (this.selectedReportType || null) !== (this.appliedFilters.selectedReportType || null) ||
        Number(this.selectedAccountOwnerId || 0) !== Number(this.appliedFilters.selectedAccountOwnerId || 0) ||
        (this.dateFrom || null) !== (this.appliedFilters.dateFrom || null) ||
        (this.dateTo || null) !== (this.appliedFilters.dateTo || null) ||
        JSON.stringify(currentVehicleIds) !== JSON.stringify(appliedVehicleIds)
    },
    requiresAccountOwnerSelection () {
      return this.showAccountOwnerFilter && !this.appliedAccountOwnerId
    },
    appliedReportParams () {
      const applied = this.appliedFilters || {}
      return {
        reportType: applied.selectedReportType || this.selectedReportType || 'distance_travelled',
        vehicleIds: Array.isArray(applied.vehicleIds) ? applied.vehicleIds : [],
        dateFrom: applied.dateFrom || null,
        dateTo: applied.dateTo || null
      }
    }
  },
  async mounted () {
    this.loadingFilters = true
    try {
      await this.loadReportTypes()
      await this.loadAccountOwners()
      this.loadFromQueryParams()
      await this.refreshVehicleList({ preserveSelectedVehicles: true })
      this.appliedAccountOwnerId = this.selectedAccountOwnerId
      this.captureAppliedFilters()
      this.componentMounted = true
    } catch (err) {
      addAlert({
        message: err,
        type: 'error'
      })
    } finally {
      this.loadingFilters = false
    }
  },
  watch: {
    selectedAccountOwnerId () {
      if (this.componentMounted && !this.isRestoringFilters) {
        this.refreshVehicleList({ preserveSelectedVehicles: false })
      }
    }
  },
  methods: {
    async loadReportTypes () {
      const response = await getReportTypes()
      this.reportTypes = Array.isArray(response) ? response : []

      if (!this.selectedReportType && this.reportTypes.length) {
        this.selectedReportType = this.reportTypes[0].reportType
      }
    },
    async loadAccountOwners () {
      if (!this.showAccountOwnerFilter) {
        this.accountOwners = []
        return
      }

      const owners = await findTransportationOwners({
        fields: { id: true, companyName: true },
        order: 'companyName ASC',
        limit: 2000
      })

      this.accountOwners = Array.isArray(owners) ? owners : []
    },
    loadFromQueryParams () {
      const query = this.$route.query || {}

      if (query.reportType) {
        this.selectedReportType = query.reportType
      }

      if (query.accountOwnerId) {
        this.selectedAccountOwnerId = Number(query.accountOwnerId)
      }

      if (query.vehicleIds) {
        this.selectedVehicleIds = String(query.vehicleIds).split(',').filter((value) => !!value)
      }

      if (query.dateFrom) {
        this.dateFrom = query.dateFrom
      }

      if (query.dateTo) {
        this.dateTo = query.dateTo
      }
    },
    async refreshVehicleList ({ preserveSelectedVehicles }) {
      let filter = {
        fields: { id: true, licensePlateNumber: true },
        order: 'licensePlateNumber ASC',
        limit: 10000
      }

      if (this.showAccountOwnerFilter && !this.selectedAccountOwnerId) {
        this.vehicles = []
        this.selectedVehicleIds = []
        return
      }

      if (this.isTransportationOwnerUser) {
        const ownerIds = (this.getSignedInUser && this.getSignedInUser.transportationOwnerUsers
          ? this.getSignedInUser.transportationOwnerUsers.map((item) => item.transportationOwnerId)
          : []).filter((value) => !!value)

        if (!ownerIds.length) {
          this.vehicles = []
          this.selectedVehicleIds = []
          return
        }

        filter.join = {
          relation: 'transportationOwnerProfile',
          scope: {
            where: ownerIds.length === 1
              ? { transportationOwnerId: ownerIds[0] }
              : { transportationOwnerId: { inq: ownerIds } }
          }
        }
      } else if (this.showAccountOwnerFilter && this.selectedAccountOwnerId) {
        filter.join = {
          relation: 'transportationOwnerProfile',
          scope: {
            where: { transportationOwnerId: this.selectedAccountOwnerId }
          }
        }
      }

      const profiles = await findTransportationProfiles(filter)
      this.vehicles = Array.isArray(profiles) ? profiles : []

      const validVehicleIds = this.vehicles.map((item) => item.id)
      const selectedVehicleIds = Array.isArray(this.selectedVehicleIds) ? this.selectedVehicleIds : []

      if (preserveSelectedVehicles) {
        this.selectedVehicleIds = selectedVehicleIds.filter((id) => validVehicleIds.includes(id))
      } else {
        this.selectedVehicleIds = []
      }
    },
    validateDateRange () {
      if ((this.dateFrom && !this.dateTo) || (!this.dateFrom && this.dateTo)) {
        addAlert({
          message: 'Please provide both date start and date end.',
          type: 'warning'
        })
        return false
      }

      if (!this.dateFrom || !this.dateTo) {
        return true
      }

      const start = moment.utc(this.dateFrom, 'YYYY-MM-DD', true)
      const end = moment.utc(this.dateTo, 'YYYY-MM-DD', true)

      if (!start.isValid() || !end.isValid()) {
        addAlert({
          message: 'Please provide valid dates in YYYY-MM-DD format.',
          type: 'warning'
        })
        return false
      }

      if (end.isBefore(start)) {
        addAlert({
          message: 'Date end must be greater than or equal to date start.',
          type: 'warning'
        })
        return false
      }

      return true
    },
    updateQueryParamsFromAppliedFilters () {
      const selectedIds = Array.isArray(this.selectedVehicleIds) ? this.selectedVehicleIds : []

      this.lastParams = addParamsToLocation({
        reportType: this.selectedReportType || null,
        accountOwnerId: this.selectedAccountOwnerId || null,
        vehicleIds: selectedIds.length ? selectedIds.join(',') : null,
        dateFrom: this.dateFrom || null,
        dateTo: this.dateTo || null
      }, this.$route, this.lastParams)
    },
    captureAppliedFilters () {
      const resolvedVehicleIds = this.resolvedVehicleIdsForQuery()
      this.appliedFilters = {
        selectedReportType: this.selectedReportType || null,
        selectedAccountOwnerId: this.selectedAccountOwnerId || null,
        selectedVehicleIds: Array.isArray(this.selectedVehicleIds) ? this.selectedVehicleIds.slice() : [],
        vehicleIds: Array.isArray(resolvedVehicleIds) ? resolvedVehicleIds.slice() : [],
        dateFrom: this.dateFrom || null,
        dateTo: this.dateTo || null
      }
    },
    async undoFilterChanges () {
      if (!this.appliedFilters) {
        return
      }

      this.isRestoringFilters = true
      try {
        this.selectedReportType = this.appliedFilters.selectedReportType
        this.selectedAccountOwnerId = this.appliedFilters.selectedAccountOwnerId
        this.selectedVehicleIds = Array.isArray(this.appliedFilters.selectedVehicleIds)
          ? this.appliedFilters.selectedVehicleIds.slice()
          : []
        this.dateFrom = this.appliedFilters.dateFrom
        this.dateTo = this.appliedFilters.dateTo
        await this.refreshVehicleList({ preserveSelectedVehicles: true })
      } finally {
        this.isRestoringFilters = false
      }
    },
    async applyFilters () {
      if (!this.validateDateRange()) {
        return
      }

      this.appliedAccountOwnerId = this.selectedAccountOwnerId
      this.captureAppliedFilters()
      this.updateQueryParamsFromAppliedFilters()
    },
    resolvedVehicleIdsForQuery () {
      if (this.showAccountOwnerFilter && !this.selectedAccountOwnerId) {
        return []
      }

      const selectedVehicleIds = Array.isArray(this.selectedVehicleIds) ? this.selectedVehicleIds : []
      if (selectedVehicleIds.length) {
        return selectedVehicleIds
      }

      if (this.isTransportationOwnerUser) {
        return (this.vehicles || []).map((item) => item.id)
      }

      if (this.showAccountOwnerFilter && this.selectedAccountOwnerId) {
        return (this.vehicles || []).map((item) => item.id)
      }

      return []
    }
  }
}
